import { Component, computed, DestroyRef, HostListener, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule, FormArray, AbstractControl } from '@angular/forms';
import { Services } from "src/app/services/api.service";
import { environment } from 'src/environments/environment';
import { CommonModule } from '@angular/common';
import { CountriesStore } from 'src/app/services/countries.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { ModalNames } from 'src/app/common/modal.config';
import { ModalService } from 'src/app/services/modal.service';
import { ErrorMessageService } from 'src/app/services/error-message.util';
import { strictEmailValidator, parseWithGoogleLibphonenumber } from 'src/app/utils/validators';
import { Country } from 'src/app/common/countries.model';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha-2';
import { RouterModule } from '@angular/router';
import { trimAllControls } from 'src/app/utils/trim-values.util';
import { FormDataStoreService } from 'src/app/services/form-data.store';
import { GdprService } from "../../services/gdpr.service";
import { SafeHtmlPipe } from "../../utils/safe-html.pipe";

@Component({
  selector: 'app-registration',
  templateUrl:  `./${environment.pageFolder}/registration.${environment.pageFolder}.component.html`,
  styleUrls:  [`./${environment.pageFolder}/registration.${environment.pageFolder}.component.sass`],
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, TranslateModule, RecaptchaModule, RouterModule, SafeHtmlPipe]
})
export class RegistrationComponent implements OnInit {
  profileForm!: FormGroup;
  redirectUrl = environment.redirectUrl;

  private readonly formDataKey = `${ModalNames.Registration}`;
  profileFormRegistration = this.formBuilder.group({
    email: ['', [ Validators.required, strictEmailValidator ]],
    firstName: ['', [ Validators.required ]],
    lastName: ['', [ Validators.required ]],
    address: ['', [ Validators.required ]],
    houseNo: ['', [ Validators.required ]],
    city: ['', [ Validators.required ]],
    postNo: ['', [ Validators.required ]],
    country: ['', [ Validators.required ]],
    phoneNo: [null, [ Validators.required, parseWithGoogleLibphonenumber ]],
    questions: this.formBuilder.array([])
  });

  coutryList: Country[] = [];
  successMessage: string = "";
  errorMessage: string = "";
  errorAlreadyHasAccountMessage: string = "";
  page = environment.page;
  private readonly gdprService = inject(GdprService);
  questionsArraySignal = signal<FormArray | null>(null);
  private readonly destroyRef = inject(DestroyRef);

  public readonly modalNamesEnum = ModalNames;
  readonly isRegistrationModalOpen = computed(() => this.modalService.modalsState().get(ModalNames.Registration) ?? false);

  @ViewChild('captchaRegister', { static: false }) captchaRegister?: RecaptchaComponent;
  recaptchaSiteKey: string = environment.recaptchaSiteKey;
  private captchaResponse: string | null = null;

  constructor(
    private readonly formBuilder: FormBuilder,
    public Services: Services,
    private readonly countriesStore: CountriesStore,
    public modalService: ModalService,
    private readonly errorMessageService: ErrorMessageService,
    private readonly formDataStore: FormDataStoreService
  ) {
    this.countriesStore.fetchCountries().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      this.coutryList = response;

      if (response.length) {
        this.profileFormRegistration.patchValue({ country: response[0].id.toString() });
      }
    })
  }

  ngOnInit(): void {
    if (environment.configId === '4' || environment.configId === '6') {
      this.formDataStore.subscribeToForm(this.formDataKey, this.profileFormRegistration, this.destroyRef);
    }

    this.gdprService.getGdprQuestions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(formGroup => {
        const formArray = formGroup.get('questions') as FormArray;
        this.profileFormRegistration.setControl('questions', formArray);
        this.questionsArraySignal.set(formArray);
      });
  }

  resolved(captchaResponse: string | null) {
    this.captchaResponse = captchaResponse;

    if (this.errorMessage === this.errorMessageService.getErrorMessage('recaptchaUnchecked')) {
      this.errorMessage = "";
    }
  }

  register() {
    this.errorMessage = "";
    this.errorAlreadyHasAccountMessage = "";
    this.successMessage = "";
    trimAllControls(this.profileFormRegistration);

    if (this.profileFormRegistration.invalid) {
      this.profileFormRegistration.markAllAsTouched();
      const controls = this.profileFormRegistration.controls;
      const missingRequired = Object.values(controls).some(control => {
        if (control.hasError('required')) {
          return true;
        }

        if (control instanceof FormArray) {
          return control.controls.some((c: AbstractControl) => c.get('answer')?.hasError('required'));
        }

        return false;
      });

      if (missingRequired) {
        this.errorMessage = this.errorMessageService.getErrorMessage('allFieldsRequired');
      } else if (controls['email'].hasError('invalidEmail')) {
        this.errorMessage = this.errorMessageService.getErrorMessage('invalidEmail');
      } else if(controls['phoneNo'].hasError('invalidPhoneNumber')) {
        this.errorMessage = this.errorMessageService.getErrorMessage('invalidPhoneNumber');
      } else {
        this.errorMessage = this.errorMessageService.getErrorMessage("error");
      }
    } else if (!this.captchaResponse) {
      this.errorMessage = this.errorMessageService.getErrorMessage('recaptchaUnchecked');
    } else {
      const formValues = this.profileFormRegistration.value;
      const countryId = Number(formValues.country);

      const questionsArray = this.questionsArraySignal();
      if (!questionsArray) {
        this.errorMessage = this.errorMessageService.getErrorMessage('error');
        return;
      }

      const selectedIds: number[] = (questionsArray.controls as AbstractControl[])
        .filter((control: AbstractControl) => control.get('answer')?.value)
        .map((control: AbstractControl) => control.get('questionId')!.value);

      const gdprAnswers = selectedIds.length ? selectedIds.join(',') + ',' : '';
      const gdprTextId = this.gdprService.gdprTextId();

      const body = {
        userData: {
          email: formValues.email,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          address: formValues.address,
          houseNo: formValues.houseNo,
          city: formValues.city,
          postNo: formValues.postNo,
          country: this.countriesStore.getCountryNameById(countryId),
          countryId: countryId,
          phoneNo: formValues.phoneNo
        },
        gdprData: {
          gdprTextId,
          gdprAnswers
        },
        captchaToken: this.captchaResponse
      };

      this.Services.registerUser(body, { headers: { 'Content-Type': 'application/json' }}).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response) => {
          if(response.success && response.data === -1) {
            this.errorAlreadyHasAccountMessage = this.errorMessageService.getErrorMessage("alreadyHasAccount");
            this.successMessage = "";

          } else if(response.success) {
            this.successMessage = this.errorMessageService.getSuccessMessage('registrationSuccess');
            this.performFormAndQuestionsReset();

            setTimeout(() => {
              this.modalService.toggleModal(ModalNames.Registration, ModalNames.Login);
              this.successMessage = '';
            }, 4000);

          } else {
            this.performFormAndQuestionsReset();
            this.successMessage = "";
            this.errorMessage = this.errorMessageService.getErrorMessage("error");
          }

          this.formDataStore.clearFormData(this.formDataKey);
          this.resetCaptcha();
        },
        error: () => {
          this.successMessage = "";
          this.errorMessage = this.errorMessageService.getErrorMessage("error");

          this.resetCaptcha();
        }
      });
    }
  }

  preventInvalidInput(event: KeyboardEvent): void {
    const allowedChars = /[0-9+\-\s()]/;

    if (!allowedChars.test(event.key)) {
      event.preventDefault();
    }
  }

  private performFormAndQuestionsReset() {
    const currentCountry = this.profileFormRegistration.get('country')?.value;

    this.profileFormRegistration.patchValue({
      email: '',
      firstName: '',
      lastName: '',
      address: '',
      houseNo: '',
      city: '',
      postNo: '',
      country: currentCountry,
      phoneNo: null
    });

    this.profileFormRegistration.markAsPristine();
    this.profileFormRegistration.markAsUntouched();

    const formArray = this.questionsArraySignal();
    formArray?.controls.forEach((control: AbstractControl) => {
      control.patchValue({ answer: false });
      control.markAsPristine();
      control.markAsUntouched();
    });
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    if (this.isRegistrationModalOpen()) {
      this.modalService.closeModal(ModalNames.Registration);
    }
  }

  private resetCaptcha(): void {
    this.captchaResponse = null;
    this.captchaRegister?.reset();
  }
}
